import { ENROLMENT_STEP, ENROLMENT_STEP_STATUS } from "./Enrolment";

export type Step = "validatingClaveUnicaLogin" | "ciDocForm" | "questionsForm";

export interface CIDocFormData {
  ciDocNum: string;
}

export interface GetQuestionsFormData {
  name: string;
  firstSurname: string;
  secondSurname: string;
  birthdate: string;
}
interface Option {
  optionId: string;
  optionText: string;
}

export interface Question {
  questionId: string;
  questionText: string;
  options: Option[];
}

interface ValidatedDoctorData {
  name: string;
  firstSurname: string;
  secondSurname: string;
  birthdate: string;
}
export interface ValidationChallengePayload {
  validatedDoctorData?: ValidatedDoctorData;
}

export interface ValidationChallengeResponse {
  questions: Question[];
}

export enum BankTransactionStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export interface GetBankTransactionChallengeResponse {
  paymentId: string;
  paymentUrl: string;
  simplifiedPaymentUrl: string;
}

export interface GetBankTransactionStatusResponse {
  bankTransferStatus: BankTransactionStatus;
  challengeStatus: BankTransactionStatus;
  validationId: string;
}

export interface PinState {
  pin: string;
  error: boolean;
  errorMessage: string;
}

export const defaultPinState: PinState = {
  pin: "",
  error: false,
  errorMessage: "",
};

export enum MekidocEsignError {
  PIN_INCORRECT = "PIN incorrecto",
  PIN_BLOCKED = "PIN bloqueado",
  UNKNOWN = "Ha ocurrido un error",
}

interface PinError {
  mekidocError: MekidocEsignError;
  esignError: string;
}
export function pinErrorMapper(errorMessage: string): PinError {
  let mekidocError: MekidocEsignError;
  if (!errorMessage) {
    mekidocError = MekidocEsignError.UNKNOWN;
  } else if (errorMessage.includes("Firma Bloqueada")) {
    mekidocError = MekidocEsignError.PIN_BLOCKED;
  } else {
    mekidocError = MekidocEsignError.PIN_INCORRECT;
  }
  return { mekidocError, esignError: errorMessage };
}

export function extractBlockingTime(errorMessage: string): string {
  const regex = /(\d+h:\d+m:\d+s)/;
  const match = errorMessage.match(regex);
  return match ? match[0] : "30 min";
}

// The waiting time of a blocked PIN, when the provider wrote it as `Xh:Ym:Zs`
// (eSign does); null when the message gives no time (BPO's is free text).
export function findBlockingTimeInSeconds(errorMessage: string): number | null {
  const regex = /(\d+)h:(\d+)m:(\d+)s/;
  const match = errorMessage.match(regex);

  if (!match) {
    return null;
  }

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const seconds = parseInt(match[3], 10);
  return hours * 3600 + minutes * 60 + seconds;
}

export type DocumentSignatureErrorKind =
  | "invalidPin"
  | "blockedPin"
  | "providerError"
  // The doctor must validate their identity in BPO before signing again.
  | "validationRequired"
  // The provider revoked the doctor's certificate: they need a new one.
  | "certificateRevoked";

export class DocumentSignatureError extends Error {
  kind: DocumentSignatureErrorKind;

  constructor(kind: DocumentSignatureErrorKind, message: string) {
    super(message);
    this.name = "DocumentSignatureError";
    this.kind = kind;
  }
}

export class EsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EsignError";
  }
}

export type QuestionsChallengeAction =
  | "block"
  | "invalidInput"
  | "retry"
  | "incompleteData";

export class QuestionsChallengeActionError extends Error {
  action: QuestionsChallengeAction;
  description?: string | React.ReactNode;
  code?: string;
  nextStep?: ENROLMENT_STEP;
  nextStepStatus?: ENROLMENT_STEP_STATUS;

  constructor(
    message: string,
    name: string,
    action: QuestionsChallengeAction,
    description?: string | React.ReactNode,
    code?: string,
    nextStep?: ENROLMENT_STEP,
    nextStepStatus?: ENROLMENT_STEP_STATUS,
  ) {
    super(message);
    this.name = name;
    this.action = action;
    this.description = description;
    this.code = code;
    this.nextStep = nextStep;
    this.nextStepStatus = nextStepStatus;
  }
}

export class UnableToGenerateQuestionsChallengesEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnableToGenerateQuestionsChallengesEsignError";
  }
}

export class DocumentNumberDoesNotMatchEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DocumentNumberDoesNotMatchEsignError";
  }
}

export class ExceededMaxRetriesQuestionsChallengeEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ExceededMaxRetriesQuestionsChallengeEsignError";
  }
}

export class IncompleteDoctorDataProvidedEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "IncompleteDoctorDataProvidedEsignError";
  }
}

export class InvalidAnswersEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidAnswersEsignError";
  }
}

export class QuestionChallengeNotAvailableEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "QuestionChallengeNotAvailableEsignError";
  }
}
